import {
  createCsvBlob
} from '../csvEncoding'

import {
  csvEscape,
  downloadBlob,
  ensureStatisticsPdfFonts,
  fileStamp,
  format2
} from './common'

const format3 = (value) => {
  const number = Number(value)
  return Number.isFinite(number) ? number.toFixed(3) : '0.000'
}

const formatChargingInfo = (data) => {
  const item = data || {}
  return `${format2(item.kwh)} kWh / ${format2(item.percent)} % / ${format2(item.cost)} €`
}

export const buildStatisticsExportBlock = (
  stats,
  periodText,
  vehicleText,
  lang,
  t
) => {
  const rows = []

  if (stats?.statsMode === 'electric') {
    const headers = [
      t('TRIPLOG.STATISTICS.EV.TOTAL_KM', {
        defaultValue: 'Najazdené'
      }),
      t('TRIPLOG.STATISTICS.EV.TOTAL_KWH', {
        defaultValue: 'Nabité'
      }),
      t('TRIPLOG.STATISTICS.EV.TOTAL_COST', {
        defaultValue: 'Náklady na nabíjanie'
      }),
      t('TRIPLOG.STATISTICS.EV.AVG_PRICE', {
        defaultValue: 'Priemerná cena'
      }),
      t('TRIPLOG.STATISTICS.EV.KWH_PER_100KM', {
        defaultValue: 'Energia / 100 km'
      }),
      t('TRIPLOG.STATISTICS.EV.COST_PER_100KM', {
        defaultValue: 'Náklady / 100 km'
      }),
      t('TRIPLOG.RECEIPTS.TABLE.CHARGING_HOME', {
        defaultValue: 'Doma'
      }),
      t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PUBLIC', {
        defaultValue: 'Verejné'
      }),
      t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PACKAGE', {
        defaultValue: 'Paušál'
      })
    ]

    rows.push([
      'EnerqiQ',
      ...headers
    ])

    rows.push([
      t('TRIPLOG.STATISTICS.MODE_EV', {
        defaultValue: 'EV'
      }),
      format2(stats?.totalKm),
      format2(stats?.totalElectricKwh),
      format2(stats?.totalReceiptCost),
      format3(stats?.avgElectricPrice),
      format2(stats?.electricKwhPer100Km),
      format2(stats?.electricCostPer100Km),
      formatChargingInfo(stats?.chargingStats?.home),
      formatChargingInfo(stats?.chargingStats?.public),
      formatChargingInfo(stats?.chargingStats?.package)
    ])

    return {
      rows,
      meta: {
        period: periodText,
        vehicle: vehicleText,
        statsMode: 'electric'
      }
    }
  }

  const headers = [
    t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_TRIPS', {
      defaultValue: 'Počet jázd'
    }),
    t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_KM', {
      defaultValue: 'Najazdené km'
    }),
    t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_COST', {
      defaultValue: 'Celkové náklady'
    }),
    t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_FUEL', {
      defaultValue: 'Množstvo paliva'
    }),
    t('TRIPLOG.STATISTICS.SUMMARY.AVG_CONSUMPTION', {
      defaultValue: 'Priemerná spotreba'
    }),
    t('TRIPLOG.STATISTICS.SUMMARY.AVG_COST_PER_KM', {
      defaultValue: 'Cena / km'
    })
  ]

  rows.push([
    'EnerqiQ',
    ...headers
  ])

  ;(stats?.vehiclesStats || []).forEach((vehicle) => {
    const km = Number(vehicle.km || 0)
    const totalCost = Number(vehicle.totalCost || 0)

    rows.push([
      vehicle.name,
      vehicle.trips ?? 0,
      format2(km),
      format2(totalCost),
      format2(vehicle.fuel),
      format2(vehicle.avgConsumption),
      km > 0
        ? format2(totalCost / km)
        : '0.00'
    ])
  })

  rows.push([
    '',
    '',
    '',
    '',
    '',
    '',
    ''
  ])

  ;(stats?.tripsChartData || []).forEach((monthItem) => {
    const monthDate = new Date(`${monthItem.month}-01`)

    const label = !Number.isNaN(monthDate.getTime())
      ? `${monthDate
          .toLocaleString(lang, {
            month: 'short'
          })
          .toUpperCase()} ${monthDate.getFullYear()}`
      : monthItem.month

    const km = Number(monthItem.total || 0)

    const costObj = (stats?.costsChartData || []).find(
      (item) => item.month === monthItem.month
    )

    const cost = Number(costObj?.cost || 0)
    const trips = Number(monthItem.tripCount || 0)

    const averageFuelPerKm =
      Number(stats?.totalKm || 0) > 0
        ? Number(stats?.totalFuel || 0) /
          Number(stats?.totalKm || 0)
        : 0

    const fuel = km * averageFuelPerKm

    const consumption =
      km > 0
        ? format2((fuel / km) * 100)
        : '0.00'

    const costPerKm =
      km > 0
        ? format2(cost / km)
        : '0.00'

    rows.push([
      label,
      trips,
      format2(km),
      format2(cost),
      format2(fuel),
      consumption,
      costPerKm
    ])
  })

  return {
    rows,
    meta: {
      period: periodText,
      vehicle: vehicleText,
      statsMode: 'fuel'
    }
  }
}

export const exportStatisticsCSV = (block, t) => {
  const titleRows = [
    [
      t('TRIPLOG.STATISTICS.TITLE', {
        defaultValue: 'Štatistiky'
      })
    ],
    [
      t('TRIPLOG.STATISTICS.SUMMARY_TAB', {
        defaultValue: 'Prehľad'
      })
    ],
    [],
    [
      t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
        defaultValue: 'Zobrazené obdobie'
      }),
      block?.meta?.period || ''
    ],
    [
      t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
        defaultValue: 'Zobrazené vozidlo'
      }),
      block?.meta?.vehicle || ''
    ],
    []
  ]

  const csv = [
    ...titleRows,
    ...(block?.rows || [])
  ]
    .map((row) => row.map(csvEscape).join(';'))
    .join('\n')

  downloadBlob(
    createCsvBlob(csv),
    `statistics_${fileStamp()}.csv`
  )
}

export const exportStatisticsXLS = async (block, t) => {
  const XLSX = await import('xlsx-js-style')

  const titleRows = [
    [
      t('TRIPLOG.STATISTICS.TITLE', {
        defaultValue: 'Štatistiky'
      })
    ],
    [
      t('TRIPLOG.STATISTICS.SUMMARY_TAB', {
        defaultValue: 'Prehľad'
      })
    ],
    [],
    [
      t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
        defaultValue: 'Zobrazené obdobie'
      }),
      block?.meta?.period || ''
    ],
    [
      t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
        defaultValue: 'Zobrazené vozidlo'
      }),
      block?.meta?.vehicle || ''
    ],
    []
  ]

  const tableRows = block?.rows || []

  const worksheetData = [
    ...titleRows,
    ...tableRows
  ]

  const ws = XLSX.utils.aoa_to_sheet(worksheetData)

  const columnCount = Math.max(
    1,
    ...worksheetData.map((row) => row.length)
  )

  ws['!cols'] = Array.from(
    {
      length: columnCount
    },
    (_, colIndex) => ({
      wch: colIndex === 0 ? 24 : 20
    })
  )

  titleRows.forEach((row, rowIndex) => {
    row.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: rowIndex,
        c: colIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: rowIndex === 0 ? 14 : 10,
          bold: true
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  const headerRowIndex = titleRows.length

  ;(tableRows[0] || []).forEach((_, colIndex) => {
    const cellAddress = XLSX.utils.encode_cell({
      r: headerRowIndex,
      c: colIndex
    })

    if (!ws[cellAddress]) return

    ws[cellAddress].s = {
      font: {
        name: 'Arial',
        sz: 10,
        bold: true
      },
      fill: {
        fgColor: {
          rgb: 'F0F0F0'
        }
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  })

  tableRows.slice(1).forEach((row, rowIndex) => {
    row.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: headerRowIndex + 1 + rowIndex,
        c: colIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(
    wb,
    ws,
    t('TRIPLOG.STATISTICS.SUMMARY_TAB', {
      defaultValue: 'Prehľad'
    })
  )

  XLSX.writeFile(
    wb,
    `statistics_${fileStamp()}.xlsx`
  )
}

export const exportStatisticsPDF = async (block, t) => {
  const { jsPDF } = await import('jspdf')
  const autoTableModule = await import('jspdf-autotable')

  const autoTable =
    autoTableModule.default ||
    autoTableModule.autoTable

  const rows = block?.rows || []
  const headers = rows[0] || []
  const dataRows = rows.slice(1)

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  })

  await ensureStatisticsPdfFonts(doc)

  const marginLeft = 18
  const marginRight = 18
  const pageWidth = doc.internal.pageSize.getWidth()
  const usableWidth = pageWidth - marginLeft - marginRight
  const columnWidth = usableWidth / Math.max(headers.length, 1)

  doc.setFont('Roboto', 'bold')
  doc.setFontSize(14)

  doc.text(
    t('TRIPLOG.STATISTICS.TITLE', {
      defaultValue: 'Štatistiky'
    }),
    marginLeft,
    28
  )

  doc.setFontSize(11)

  doc.text(
    t('TRIPLOG.STATISTICS.SUMMARY_TAB', {
      defaultValue: 'Prehľad'
    }),
    marginLeft,
    44
  )

  doc.setFontSize(9)

  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
      defaultValue: 'Zobrazené obdobie'
    })}: ${block?.meta?.period || ''}`,
    marginLeft,
    60
  )

  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
      defaultValue: 'Zobrazené vozidlo'
    })}: ${block?.meta?.vehicle || ''}`,
    marginLeft,
    74
  )

  autoTable(doc, {
    head: [headers],
    body: dataRows,
    startY: 88,
    theme: 'grid',
    margin: {
      top: 18,
      right: marginRight,
      bottom: 18,
      left: marginLeft
    },
    tableWidth: usableWidth,
    styles: {
      font: 'Roboto',
      fontStyle: 'normal',
      fontSize: block?.meta?.statsMode === 'electric' ? 7 : 8,
      cellPadding: 3,
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'left',
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
      minCellHeight: 12
    },
    headStyles: {
      font: 'Roboto',
      fontStyle: 'bold',
      fontSize: block?.meta?.statsMode === 'electric' ? 7 : 8,
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineWidth: 0.4,
      lineColor: [0, 0, 0]
    },
    bodyStyles: {
      font: 'Roboto',
      fontStyle: 'normal',
      textColor: [0, 0, 0],
      lineWidth: 0.3,
      lineColor: [0, 0, 0]
    },
    columnStyles: headers.reduce(
      (acc, _header, index) => {
        acc[index] = {
          cellWidth: columnWidth
        }

        return acc
      },
      {}
    ),
    horizontalPageBreak: false,
    pageBreak: 'auto',
    rowPageBreak: 'avoid'
  })

  downloadBlob(
    doc.output('blob'),
    `statistics_${fileStamp()}.pdf`
  )
}